import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Pipette, Plus } from 'lucide-react';
import { type CSSProperties, type KeyboardEvent, type PointerEvent, useId, useRef, useState } from 'react';

import { cn } from '@/design-system/lib/cn';
import { type Hsv, clamp, hexToHsv, hsvToHex, hueColor, parseHex } from '@/design-system/lib/color';

/** O conta-gotas do navegador (Chrome e Edge no computador); onde não há, o botão some. */
type EyeDropperApi = { open: () => Promise<{ sRGBHex: string }> };
type EyeDropperWindow = Window & { EyeDropper?: new () => EyeDropperApi };

function eyeDropper(): (new () => EyeDropperApi) | undefined {
  return typeof window === 'undefined' ? undefined : (window as EyeDropperWindow).EyeDropper;
}

/** Passo das setas (1%, ou 1° no matiz); com Shift, dez vezes. */
function keyStep(e: KeyboardEvent, unit: number): number {
  return e.shiftKey ? unit * 10 : unit;
}

/** As paradas do trilho do matiz, de 0° a 360°, calculadas (não há hex escrito à mão). */
const HUE_GRADIENT = `linear-gradient(to right, ${[0, 60, 120, 180, 240, 300, 360].map(hueColor).join(', ')})`;
const HUE_RING = `conic-gradient(${[0, 60, 120, 180, 240, 300, 360].map(hueColor).join(', ')})`;

/** A posição do ponteiro dentro do elemento, de 0 a 1 em cada eixo. */
function pointerAt(e: PointerEvent<HTMLElement>): { x: number; y: number } | null {
  const box = e.currentTarget.getBoundingClientRect();

  if (!box.width || !box.height) return null;

  return { x: clamp((e.clientX - box.left) / box.width), y: clamp((e.clientY - box.top) / box.height) };
}

/** Arrastar com o ponteiro: captura no `pointerdown` e segue no `pointermove`. */
function useDrag(onMove: (at: { x: number; y: number }) => void) {
  const dragging = useRef(false);

  return {
    onPointerDown: (e: PointerEvent<HTMLElement>) => {
      if (e.button !== 0) return;

      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      e.currentTarget.focus();
      dragging.current = true;

      const at = pointerAt(e);
      if (at) onMove(at);
    },
    onPointerMove: (e: PointerEvent<HTMLElement>) => {
      if (!dragging.current) return;

      const at = pointerAt(e);
      if (at) onMove(at);
    },
    onPointerUp: () => {
      dragging.current = false;
    },
    onPointerCancel: () => {
      dragging.current = false;
    },
  };
}

const THUMB =
  'pointer-events-none absolute size-4 -translate-x-1/2 -translate-y-1/2 rounded-pill border-2 border-white shadow-popover ring-1 ring-black/20';

const FOCUSABLE =
  'outline-none focus-visible:ring-3 focus-visible:ring-primary-focus/40 focus-visible:ring-offset-1 focus-visible:ring-offset-white';

export type ColorPickerProps = {
  /** `#RRGGBB`. */
  value: string;
  /** Recebe `#RRGGBB` em maiúsculas a cada mudança (arrastar, setas, hex, conta-gotas). */
  onChange: (hex: string) => void;
  className?: string;
};

/**
 * Seletor de cor livre: a área de saturação × brilho, o trilho do matiz, o campo
 * hexadecimal e, onde o navegador oferece, o conta-gotas. Tudo pelo teclado — as duas
 * áreas são `slider` com setas (Shift dá passos de 10), Home e End — e pelo ponteiro
 * (mouse, toque e caneta, com captura). O valor por fora é sempre `#RRGGBB`.
 */
export function ColorPicker({ value, onChange, className }: ColorPickerProps) {
  const hexId = useId();
  const [hsv, setHsv] = useState<Hsv>(() => hexToHsv(value));
  const [seen, setSeen] = useState(value);
  const [text, setText] = useState(value.toUpperCase());
  const Dropper = eyeDropper();
  const current = hsvToHex(hsv);

  // Valor trocado por fora (outra cor da paleta): a área e o campo acompanham. Quando é a
  // própria cor que o seletor acabou de emitir, nada muda — o HSV interno guarda o matiz
  // de quando a cor fica cinza, branca ou preta.
  if (value !== seen) {
    setSeen(value);

    if (value.toUpperCase() !== current) {
      setHsv(hexToHsv(value));
      setText(value.toUpperCase());
    }
  }

  const commit = (next: Hsv, hex = hsvToHex(next)) => {
    setHsv(next);
    if (hex !== current) onChange(hex);
  };

  const emit = (next: Hsv) => {
    setText(hsvToHex(next));
    commit(next);
  };

  const area = useDrag(({ x, y }) => emit({ ...hsv, s: x, v: 1 - y }));
  const hue = useDrag(({ x }) => emit({ ...hsv, h: Math.round(x * 360) }));

  const areaKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = keyStep(e, 0.01);
    const moves: Record<string, Partial<Hsv>> = {
      ArrowLeft: { s: clamp(hsv.s - step) },
      ArrowRight: { s: clamp(hsv.s + step) },
      ArrowUp: { v: clamp(hsv.v + step) },
      ArrowDown: { v: clamp(hsv.v - step) },
      Home: { s: 0 },
      End: { s: 1 },
    };
    const move = moves[e.key];

    if (!move) return;

    e.preventDefault();
    emit({ ...hsv, ...move });
  };

  const hueKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = keyStep(e, 1);
    const moves: Record<string, number> = {
      ArrowLeft: hsv.h - step,
      ArrowDown: hsv.h - step,
      ArrowRight: hsv.h + step,
      ArrowUp: hsv.h + step,
      Home: 0,
      End: 360,
    };
    const next = moves[e.key];

    if (next === undefined) return;

    e.preventDefault();
    emit({ ...hsv, h: clamp(next, 0, 360) });
  };

  /** A cor digitada ou colada; o matiz guardado só muda quando a cor tem matiz. */
  const takeHex = (hex: string) => {
    const next = hexToHsv(hex);

    commit(next.s && next.v ? next : { ...next, h: hsv.h }, hex);
  };

  // Enquanto se digita, só o hex completo de 6 dígitos vale: "3a7" seria #33AA77 no
  // meio de "3a7d44". O curto (#FA0) vale ao sair do campo.
  const typeHex = (raw: string) => {
    setText(raw.toUpperCase());

    if (raw.trim().replace(/^#/, '').length === 6) {
      const hex = parseHex(raw);

      if (hex) takeHex(hex);
    }
  };

  const leaveHex = () => {
    const hex = parseHex(text);

    if (hex) takeHex(hex);
    setText(hex ?? current);
  };

  const pickFromScreen = async () => {
    if (!Dropper) return;

    try {
      const { sRGBHex } = await new Dropper().open();
      const hex = parseHex(sRGBHex);

      if (hex) {
        setText(hex);
        takeHex(hex);
      }
    } catch {
      // Cancelado com Esc: fica a cor de antes.
    }
  };

  const saturation = Math.round(hsv.s * 100);
  const brightness = Math.round(hsv.v * 100);
  // Vermelho só com o que nunca vira cor (letra fora do hex, dígito a mais); o hex pela
  // metade é só digitação em curso.
  const invalid = !/^#?[0-9A-F]{0,6}$/i.test(text.trim());

  return (
    <div className={cn('flex w-60 flex-col gap-3', className)}>
      <div
        role="slider"
        tabIndex={0}
        aria-label="Saturação e brilho"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={saturation}
        aria-valuetext={`Saturação ${saturation}%, brilho ${brightness}%`}
        onKeyDown={areaKey}
        {...area}
        className={cn('relative h-36 cursor-crosshair touch-none rounded-control-sm', FOCUSABLE)}
        style={{
          backgroundColor: hueColor(hsv.h),
          backgroundImage:
            'linear-gradient(to top, black, transparent), linear-gradient(to right, white, transparent)',
        }}
      >
        <span
          aria-hidden
          className={THUMB}
          style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, backgroundColor: current }}
        />
      </div>

      <div
        role="slider"
        tabIndex={0}
        aria-label="Matiz"
        aria-valuemin={0}
        aria-valuemax={360}
        aria-valuenow={Math.round(hsv.h)}
        aria-valuetext={`${Math.round(hsv.h)}°`}
        onKeyDown={hueKey}
        {...hue}
        className={cn('relative h-3 cursor-pointer touch-none rounded-pill', FOCUSABLE)}
        style={{ backgroundImage: HUE_GRADIENT }}
      >
        <span
          aria-hidden
          className={cn(THUMB, 'top-1/2')}
          style={{ left: `${(hsv.h / 360) * 100}%`, backgroundColor: hueColor(hsv.h) }}
        />
      </div>

      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className="size-9 shrink-0 rounded-control-sm border border-line-input"
          style={{ backgroundColor: current }}
        />
        <label htmlFor={hexId} className="sr-only">
          Hexadecimal
        </label>
        <input
          id={hexId}
          value={text}
          maxLength={7}
          spellCheck={false}
          autoComplete="off"
          aria-invalid={invalid || undefined}
          onChange={(e) => typeHex(e.target.value)}
          onBlur={leaveHex}
          className={cn(
            'h-9 w-full min-w-0 rounded-control-sm border bg-white px-2.5 font-mono text-small text-ink uppercase',
            'focus:border-primary-focus focus:ring-3 focus:ring-primary-focus/15 focus:outline-none',
            invalid ? 'border-status-conflict' : 'border-line-input'
          )}
        />
        {Dropper ? (
          <button
            type="button"
            aria-label="Pegar uma cor da tela"
            title="Pegar uma cor da tela"
            onClick={() => void pickFromScreen()}
            className="grid size-9 shrink-0 place-items-center rounded-control-sm border border-line-input text-ink-muted hover:bg-surface-muted hover:text-primary"
          >
            <Pipette aria-hidden className="size-4 stroke-[1.8]" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

export type ColorPickerButtonProps = {
  value: string;
  onChange: (hex: string) => void;
  /**
   * A cor atual está fora das opções prontas: o botão aparece marcado, preenchido com ela.
   * Senão, mostra o arco-íris com o "+".
   */
  selected: boolean;
  /** Nome do botão e do painel. */
  label?: string;
  /** Tamanho e anel de marcado, iguais aos das outras amostras da fileira. */
  className?: string;
  selectedClassName?: string;
  /** Para o anel `ring-current`: a cor do anel acompanha a amostra, como nas outras. */
  style?: CSSProperties;
};

/**
 * A amostra "Cor personalizada" ao fim de uma fileira de cores (`radiogroup`): é uma das
 * opções (`role="radio"`, marcada quando a cor não é uma das prontas) e abre o
 * `ColorPicker` num popover. A cor muda enquanto se escolhe; Esc ou clicar fora fecha.
 */
export function ColorPickerButton({
  value,
  onChange,
  selected,
  label = 'Cor personalizada',
  className,
  selectedClassName,
  style,
}: ColorPickerButtonProps) {
  const name = selected ? `${label} · ${value.toUpperCase()}` : label;

  return (
    <PopoverPrimitive.Root modal>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          role="radio"
          aria-checked={selected}
          aria-label={name}
          title={name}
          className={cn(
            'relative grid place-items-center rounded-pill',
            FOCUSABLE,
            className,
            selected && selectedClassName
          )}
          style={{
            ...style,
            ...(selected ? { backgroundColor: value, color: value } : { backgroundImage: HUE_RING }),
          }}
        >
          {selected ? null : (
            <span aria-hidden className="grid size-4 place-items-center rounded-pill bg-white text-ink">
              <Plus className="size-3 stroke-[2.5]" />
            </span>
          )}
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          side="bottom"
          sideOffset={8}
          collisionPadding={8}
          aria-label={label}
          className={cn(
            'z-[90] rounded-block border border-line-input bg-white p-3 shadow-popover outline-none',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95'
          )}
        >
          <ColorPicker value={value} onChange={onChange} />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
